import { useState, useRef, useEffect, useCallback } from "react";

// ── SCROLL-ADJUSTABLE INPUT ───────────────────────────────────────────────────
export function ScrollInput({ value, hint, step, min = 0, onChange, style, ...rest }) {
  const ref = useRef();

  // Keep latest props in a ref so mount-only event listeners always see current values
  const propsRef = useRef({ value, hint, step, min, onChange });
  useEffect(() => { propsRef.current = { value, hint, step, min, onChange }; });

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const fire = (sign) => {
      const { value, hint, step, min, onChange } = propsRef.current;
      const current = parseFloat(value) || parseFloat(hint) || 0;
      const next = Math.max(min, current + sign * step);
      const str = step >= 1 ? String(Math.round(next)) : String(parseFloat(next.toFixed(2)));
      onChange(str === "0" && min > 0 ? String(min) : str);
    };

    const onWheel = (e) => { e.preventDefault(); fire(e.deltaY < 0 ? 1 : -1); };

    let touchY = null;
    let accum = 0;
    const onTouchStart = (e) => { touchY = e.touches[0].clientY; accum = 0; };
    const onTouchMove  = (e) => {
      if (touchY === null) return;
      e.preventDefault();
      const dy = touchY - e.touches[0].clientY;
      touchY = e.touches[0].clientY;
      accum += dy;
      const THRESH = 20;
      if (Math.abs(accum) >= THRESH) {
        const steps = Math.floor(Math.abs(accum) / THRESH);
        fire(accum > 0 ? steps : -steps);
        accum -= (accum > 0 ? steps : -steps) * THRESH;
      }
    };
    const onTouchEnd = () => { touchY = null; accum = 0; };

    el.addEventListener("wheel",      onWheel,      { passive: false });
    el.addEventListener("touchstart", onTouchStart, { passive: false });
    el.addEventListener("touchmove",  onTouchMove,  { passive: false });
    el.addEventListener("touchend",   onTouchEnd);
    return () => {
      el.removeEventListener("wheel",      onWheel);
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove",  onTouchMove);
      el.removeEventListener("touchend",   onTouchEnd);
    };
  }, []); // mount-only — propsRef keeps values fresh

  return (
    <input
      ref={ref}
      type="text"
      inputMode="decimal"
      value={value}
      placeholder={hint || rest.placeholder || ""}
      onChange={e => onChange(e.target.value)}
      style={{
        width: "100%",
        background: "var(--bg2)",
        border: "1px solid var(--border)",
        borderRadius: 8,
        padding: "12px 4px",
        fontSize: 18,
        fontWeight: 600,
        textAlign: "center",
        color: "var(--text)",
        fontFamily: "inherit",
        outline: "none",
        ...style,
      }}
      {...rest}
    />
  );
}

// ── HOLD TO DELETE ────────────────────────────────────────────────────────────
export function HoldToDelete({ onDelete, holdMs = 1000, label = "DELETE", fullWidth = false }) {
  const [progress, setProgress] = useState(0);
  const [holding, setHolding]   = useState(false);
  const intervalRef = useRef(null);
  const TICK = 30;

  const start = (e) => {
    e.stopPropagation();
    setHolding(true);
    intervalRef.current = setInterval(() => {
      setProgress(p => {
        const next = p + (TICK / holdMs) * 100;
        if (next >= 100) {
          clearInterval(intervalRef.current);
          onDelete();
          return 100;
        }
        return next;
      });
    }, TICK);
  };

  const stop = (e) => {
    e?.stopPropagation();
    clearInterval(intervalRef.current);
    setHolding(false);
    setProgress(0);
  };

  useEffect(() => () => clearInterval(intervalRef.current), []);

  return (
    <div style={{ position:"relative", borderRadius:6, overflow:"hidden", display: fullWidth ? "flex" : "inline-flex", width: fullWidth ? "100%" : undefined }}
      onMouseDown={start} onMouseUp={stop} onMouseLeave={stop}
      onTouchStart={start} onTouchEnd={stop}
    >
      <div style={{
        position:"absolute", inset:0,
        background:"rgba(255,85,85,0.3)",
        width:`${progress}%`,
        transition: holding ? "none" : "width 0.15s",
        pointerEvents:"none",
        borderRadius:6,
      }}/>
      <button
        style={{
          position:"relative", padding:"5px 10px", width: fullWidth ? "100%" : undefined,
          background:"rgba(255,85,85,0.1)",
          border:"1px solid rgba(255,85,85,0.25)",
          color: holding ? "#ff6b6b" : "rgba(255,100,100,0.6)",
          fontFamily:"'Space Grotesk',sans-serif",
          fontSize:10, fontWeight:700, letterSpacing:"0.1em",
          textTransform:"uppercase", cursor:"pointer", borderRadius:6,
          transition:"color 0.15s",
          userSelect:"none", WebkitUserSelect:"none",
        }}
      >{label}</button>
    </div>
  );
}

// ── FORM HELPERS ─────────────────────────────────────────────────────────────
export const Req = () => <span className="req-star">*</span>;

export function FormLabel({ children, required }) {
  return <div className="form-label">{children}{required && <Req />}</div>;
}

// ── CHART TOOLTIP ─────────────────────────────────────────────────────────────
export function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="ctooltip">
      <div className="ctooltip-label">{label}</div>
      {payload.map((p, i) => (
        <div key={i} className="ctooltip-val" style={{ color: p.color }}>{p.name}: {p.value}</div>
      ))}
    </div>
  );
}

// ── AVATAR / COLOR HELPERS ────────────────────────────────────────────────────
const AVATAR_COLORS = ["accent", "teal", "green", "orange"];
export const avatarColor = (name = "") =>
  AVATAR_COLORS[(name?.charCodeAt(0) || 0) % AVATAR_COLORS.length];

// Same palette used for exercise/workout initials
export const initColor = avatarColor;

export function AvatarCircle({ name, size, fontSize }) {
  const style = size
    ? { width: size, height: size, fontSize: fontSize || Math.floor(size * 0.34) }
    : undefined;
  return (
    <div className={`avatar-circle avatar-${avatarColor(name)}`} style={style}>
      {(name || "?").slice(0, 2).toUpperCase()}
    </div>
  );
}

export function UserCell({ name }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <AvatarCircle name={name} />
      <span style={{ fontWeight: 600 }}>{name}</span>
    </div>
  );
}

// ── EMPTY STATE ───────────────────────────────────────────────────────────────
export function EmptyState({ title, desc }) {
  return (
    <div className="empty-state">
      {title && <div className="empty-title">{title}</div>}
      {desc  && <div className="empty-desc">{desc}</div>}
    </div>
  );
}

// ── STAT CARD ─────────────────────────────────────────────────────────────────
export function StatCard({ label, value, icon, color = "accent" }) {
  return (
    <div className="stat-card">
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10 }}>
        <div>
          <div className="stat-label">{label}</div>
          <div className="stat-value" style={{ color: `var(--${color})` }}>{value}</div>
        </div>
        {icon && <div className={`stat-icon stat-icon-${color}`}>{icon}</div>}
      </div>
    </div>
  );
}

// ── EXERCISE SEARCH DROPDOWN ──────────────────────────────────────────────────
export function ExerciseSearchDropdown({ items, value, onChange, label = "Exercise", placeholder = "Search exercise..." }) {
  const [search, setSearch] = useState("");
  const [open, setOpen]     = useState(false);

  const filtered = search.trim()
    ? items.filter(n => n.toLowerCase().includes(search.toLowerCase()))
    : items;

  const select = (name) => { onChange(name); setSearch(""); setOpen(false); };
  const clear  = ()     => { onChange(null); setSearch(""); setOpen(false); };

  return (
    <div style={{ position: "relative" }}>
      {label && <div className="form-label" style={{ marginBottom: 6 }}>{label}</div>}
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <input
          className="input"
          type="text"
          placeholder={placeholder}
          value={search}
          onChange={e => { setSearch(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          style={{ flex: 1 }}
        />
        {value && (
          <button className="filter-btn active" style={{ whiteSpace: "nowrap", flexShrink: 0 }} onClick={clear}>
            {value} ×
          </button>
        )}
      </div>
      {open && search.trim() && (
        <div style={{
          position: "absolute", top: "100%", left: 0, right: 0,
          background: "var(--bg2)", border: "1px solid var(--border2)",
          borderRadius: 8, zIndex: 50, maxHeight: 220, overflowY: "auto",
          marginTop: 4, boxShadow: "0 8px 24px rgba(0,0,0,0.25)",
        }}>
          {filtered.length === 0
            ? <div style={{ padding: "10px 14px", color: "var(--muted)", fontSize: 13 }}>No matches</div>
            : filtered.map(name => (
              <div key={name} onMouseDown={() => select(name)} style={{
                padding: "9px 14px", cursor: "pointer", fontSize: 13,
                borderBottom: "1px solid var(--border)",
                color: value === name ? "var(--accent)" : "var(--text)",
              }}>
                {name}
              </div>
            ))
          }
        </div>
      )}
    </div>
  );
}
